import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { TreeNode } from 'primereact/treenode';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';
import { markDeletedItems } from 'utils/markDeletedItems';

import { AttainmentService } from 'components/services';
import { MeasureUnitService } from 'components/services/MeasureUnitService';

import ServiceResult from '../components/services/interfaces/ServiceResult';
import { OperationTypeSchema } from '../constants/schemas';
import useOperationTypesService from '../hooks/services/useOperationTypesService';
import { useApiCall } from '../hooks/useApiCall';
import { DropdownSelectorType, MeasureUnitUI, OperationType } from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { OperationTypeProps } from '../types/props';
import { PickerTexts } from '../utils';

import '../ui/css/OperationTypesCard.css';
import { CustomDropdown } from './custom-controls';
import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { TagsComponent } from './TagsComponent';

interface RefInterface {
  handleCancel: () => void;
}

const OperationTypeCard = React.forwardRef<RefInterface, OperationTypeProps>(
  (
    {
      operationTypeProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog
    }: OperationTypeProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(OperationTypeSchema),
      defaultValues: operationTypeProps
    });
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [, forceUpdate] = useState<number>(0);
    const toast = useRef<Toast>(null);
    const operationName = watch('operationName');
    const measure = watch('measure');
    const operationTypeId = watch('id');
    const attainments = watch('attainments');

    const selectedAttainments = useMemo(() => {
      if (!attainments) return [];
      if (Array.isArray(attainments)) {
        return attainments as TreeNode[];
      }
      return [attainments as TreeNode];
    }, [attainments]);

    const [selectedUnitMeasurement, setSelectedUnitMeasurement] = useState<
      DropdownSelectorType | undefined
    >();
    const [measurments, setMeasurments] = useState<MeasureUnitUI[]>([]);

    const getMeasures = useCallback(async () => {
      const measures = await MeasureUnitService.getMeasureUnitForSelection(); 
      setMeasurments(measures.data.value);
    }, []);
    useApiCall(getMeasures);

    const [attainmentOptions, setAttainmentOptions] = useState<TreeNode[]>([]);

    useEffect(() => {
      const fetchSchedules = async () => {
        const resultAttainment = await AttainmentService.getAttainmentsForSelection();
        setAttainmentOptions(resultAttainment.data.value.itemsList ?? []);
      };
      fetchSchedules();
      
    }, [operationTypeId]);

    const handleDataTableDropdownSelectionChange = (
      selected: TreeNode | TreeNode[] | null
    ) => {
      if (Array.isArray(selected)) {
        setValue('attainments', selected, {
          shouldDirty: true,
          shouldValidate: true
        });
      } else if (selected && !Array.isArray(selected)) {
        setValue('attainments', [selected], {
          shouldDirty: true,
          shouldValidate: true
        });
      } else {
        setValue('attainments', [], {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    };

    const operationService = useOperationTypesService();
    const getOperationTypesCallback = useCallback(() => {
      let serviceResult: ServiceResult<OperationType | undefined> | undefined =
        undefined;
      const id = operationTypeProps.id?.toString();
      if (id && id !== '') {
        serviceResult = operationService.get(parseInt(id));
        serviceResult.result.then((x) => {
          if (x) {
            setSelectedUnitMeasurement(x.measure);
            reset(x);
          }
        });
      }

      return () => serviceResult?.cancel();
    }, [operationTypeProps.id, operationService, reset]);

    useEffect(() => {
      const cancel = getOperationTypesCallback();
      return () => cancel();
    }, [getOperationTypesCallback]);

    const onSubmit: SubmitHandler<OperationType> = useCallback(
      async (data) => {
        const newData = { ...data };
        const dataId = data.id;
        let result: number | undefined = 0;
        
        if (selectedAttainments && selectedAttainments.length > 0) {
          newData.attainments = selectedAttainments;
        } else {
          newData.attainments = [];
        }
        
        if (selectedUnitMeasurement)
          newData.measure = selectedUnitMeasurement as any;
        const isIdExist =
          data && dataId !== undefined && dataId !== null && dataId !== '';
        if (onUpdate && isIdExist) {
          result = await onUpdate(dataId, newData);
        }
        if (onAdd && !isIdExist) {
          newData.id = 0;
          result = await onAdd(newData);
        }
        if (result === -1) {
          setError('operationName', {
            type: 'value',
            message:
              'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название'
          });
        } else {
          closeDialog && closeDialog();
        }
      },
      [closeDialog, onAdd, onUpdate, selectedUnitMeasurement, setError, selectedAttainments]
    );

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (closeDialog && !isDirty) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
      // Perform navigation/close the card without saving
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = async () => {
      if (onDelete) {
        await onDelete(operationTypeId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleDeleteTag = (operationTypeId: string | number) => {
      const newSelectedAttainments = selectedAttainments.filter(
        (selectedAttainmentsOption) =>
          selectedAttainmentsOption.id?.toString() !== operationTypeId.toString()
      );
      setValue('attainments', newSelectedAttainments, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const dropdownPlaceholder = measure?.label ?? 'Выберите единицу измерения'

    return (
      <div className="dialog-tabless-card operationTypes-scores-card operationTypes-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          <div className="operationType-card__column">
            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center w-full">
                  <label htmlFor="value" className="p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="asset-characteristic-card__input"
                      {...register('operationName')}
                      style={{width: '100%'}}
                    />
                  </div>
                </div>
                <p
                  className={`operationTypes-scores-card-errors card-errors ${
                    errors.operationName ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.operationName ? errors.operationName.message : ' '} 
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="measure" className="p-0">
                    Единица изменения:
                  </label>
                  <div className="p-0 col-8">
                    <Dropdown
                      filter
                      showClear
                      value={selectedUnitMeasurement}
                      onChange={(e: DropdownChangeEvent) => {
                        const selected: ObjectForSelection | undefined = e.value
                          ? {
                            id: parseInt(e.value.id.toString()),
                            label: e.value.label as string
                          }
                          : undefined;
                        setSelectedUnitMeasurement(selected);
                        setValue('measure', selected, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                        forceUpdate((prev) => prev + 1);
                      }}
                      options={measurments}
                      placeholder={measure?.label ?? 'Выберите единицу измерения'}
                      className="w-full asset-characteristic-card__input"
                      width="100%"
                      style={{height: 42}}
                      valueTemplate={(option) => (
                        <DropdownWithDeletedValueTemplate 
                          option={option} 
                          selectedItem={selectedUnitMeasurement}
                          placeholder={measure?.label ?? 'Выберите единицу измерения'}
                        />
                      )}
                    />
                  </div>

                </div>                  
                <p
                  className={`operationTypes-scores-card-errors card-errors ${
                    errors.measure ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.measure ? (errors.measure as any).message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="attainments" className="p-0">
                    Навыки и допуски:
                  </label>
                  <div className="p-0 col-8">
                    <CustomDropdown<TreeNode>
                      multiplySelectMode
                      assetOptions={attainmentOptions}
                      selectedAsset={selectedAttainments}
                      onSelectionChange={handleDataTableDropdownSelectionChange}
                      placeholder={PickerTexts.attainments(selectedAttainments.length)}
                      emptyMessage="Нет навыков и допусков для выбора."
                      fieldName="name"
                    />
                  </div>
                </div>
                <p
                  className={`task-type-card-errors card-errors ${
                    errors.attainments
                      ? 'text-red-500'
                      : 'text-transparent'
                  } `}
                >
                  {errors.attainments
                    ? (errors.attainments as any)?.message
                    : ' '}
                </p>
              </div>
            </div>

            <div className="grid flex justify-content-between align-items-center">
              <label style={{ width: '130px' }}></label>
              {selectedAttainments?.length > 0 && (
                <div
                  style={{
                    width: '100%',
                    maxHeight: '75px',
                    overflow: 'auto',
                    marginBottom: selectedAttainments?.length > 0 ? 16 : 0
                  }}
                  id="tags-scroll"
                >
                  <TagsComponent
                    passedClassName="gap-2"
                    initialTagsItems={markDeletedItems<TreeNode>(selectedAttainments, attainmentOptions) as any}
                    onDelete={handleDeleteTag}
                    fieldName="name"
                  />
                </div>
              )}
              {(!selectedAttainments || selectedAttainments.length < 1) && (
                <div
                  style={{
                    width: '100%',
                    maxHeight: '75px',
                    overflow: 'auto'
                  }}
                  id="tags-scroll"
                >
                </div>
              )}
            </div>

            
            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center w-full">
                  <label htmlFor="value" className="p-0">
                    Трудозатраты (мин):
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="asset-characteristic-card__input" 
                      type="number"         
                      {...register('effort', {
                        setValueAs: (value) => value === '' ? undefined : Number(value)
                      })}
                      style={{width: '100%'}}
                    />
                  </div>
                </div>
                <p
                  className={`operationTypes-scores-card-errors card-errors ${
                    errors.effort ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.effort ? errors.effort.message : ' '} 
                </p>
              </div>
            </div>
            

          </div>
        </div>

        <div className="operationType-card__buttons default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="вид работ"
          entityName={operationName}
        />
      </div>
    );
  }
);

OperationTypeCard.displayName = 'OperationTypeCard';

export default OperationTypeCard;
